import { z } from 'zod';

// ===================
// SANITIZATION UTILS
// ===================

/**
 * Sanitize text input by trimming, normalizing whitespace, and stripping HTML tags
 */
export function sanitizeText(input: string): string {
  return input
    .trim()
    .replace(/\s+/g, ' ')  // Normalize whitespace
    .replace(/<[^>]*>/g, ''); // Strip HTML tags
}

/**
 * Sanitize for code/technical identifiers (snake_case)
 */
export function sanitizeFieldName(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ===================
// COMMON SCHEMAS
// ===================

/**
 * Reusable email schema with enhanced validation
 */
export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .max(255, 'Email must be less than 255 characters')
  .email('Please enter a valid email address')
  .refine(
    (email) => !email.includes('..') && !email.startsWith('.'),
    'Invalid email format'
  );

/**
 * Name schema - blocks obvious spam patterns
 */
export const nameSchema = z
  .string()
  .trim()
  .min(2, 'Name must be at least 2 characters')
  .max(100, 'Name must be less than 100 characters')
  .refine(
    (name) => !/^[0-9]+$/.test(name),
    'Name cannot be only numbers'
  )
  .refine(
    (name) => !/(http|www\.|\.com|\.net|\.org)/i.test(name),
    'Name cannot contain URLs'
  );

/**
 * Organization schema - optional with reasonable limits
 */
export const organizationSchema = z
  .string()
  .trim()
  .max(100, 'Organization must be less than 100 characters')
  .optional()
  .transform(val => val || undefined);

/**
 * Message/description schema with spam detection
 */
export const messageSchema = z
  .string()
  .trim()
  .min(10, 'Must be at least 10 characters')
  .max(2000, 'Must be less than 2000 characters')
  .refine(
    (msg) => {
      // Count URLs - too many is suspicious
      const urlCount = (msg.match(/https?:\/\//gi) || []).length;
      return urlCount <= 5;
    },
    'Contains too many links'
  );

/**
 * Long text schema for descriptions and justifications
 */
export const longTextSchema = z
  .string()
  .trim()
  .min(20, 'Must be at least 20 characters')
  .max(2000, 'Must be less than 2000 characters')
  .refine(
    (msg) => {
      const urlCount = (msg.match(/https?:\/\//gi) || []).length;
      return urlCount <= 5;
    },
    'Contains too many links'
  );

/**
 * Title schema - for proposal/feature titles
 */
export const titleSchema = z
  .string()
  .trim()
  .min(5, 'Title must be at least 5 characters')
  .max(200, 'Title must be less than 200 characters')
  .refine(
    (title) => !/(http|www\.|\.com)/i.test(title),
    'Title cannot contain URLs'
  );

/**
 * Subject schema - for optional subjects
 */
export const subjectSchema = z
  .string()
  .trim()
  .max(200, 'Subject must be less than 200 characters')
  .optional()
  .transform(val => val || undefined);

/**
 * Field name schema (for proposals) - must be snake_case
 */
export const fieldNameSchema = z
  .string()
  .trim()
  .min(2, 'Field name must be at least 2 characters')
  .max(50, 'Field name must be less than 50 characters')
  .regex(
    /^[a-z][a-z0-9_]*$/,
    'Must be lowercase with underscores (e.g., field_name)'
  )
  .optional()
  .transform(val => val || undefined);

/**
 * Enum value schema - must be UPPERCASE_SNAKE_CASE
 */
export const enumValueSchema = z
  .string()
  .trim()
  .max(50, 'Enum value must be less than 50 characters')
  .regex(
    /^[A-Z][A-Z0-9_]*$/,
    'Must be UPPERCASE with underscores (e.g., ENUM_VALUE)'
  )
  .optional()
  .transform(val => val || undefined);
